import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Grid,
    CircularProgress,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormControl,
    IconButton,
    Modal,
    Slider,
    Tooltip,
} from '@mui/material';
import { useCardContext } from 'contexts/CardContext';
import { useParams } from 'react-router-dom';
import { getCardById, updateCard } from 'apis/cardApi';
import NotificationSnackbar from 'components/common/notification/NotificationSnackbar';
import { InfoOutlinedIcon, InsertPhotoOutlinedIcon, CloseIcon } from 'icons/materialIcons';
import Cropper from 'react-easy-crop';
import getCroppedImg from 'utils/images/cropImageUtils';

const ProductsAndServicesContainer = () => {
    // Use refs to track if we've already performed certain operations
    const initializedRef = useRef(false);
    const fetchedCardDetailsRef = useRef(false);

    // State for form fields
    const [formState, setFormState] = useState({
        heading: '',
        description: '',
        link: '',
        callToAction: '',
        mediaType: 'image',
        videoLink: ''
    });

    // Image upload states
    const [image, setImage] = useState(null);
    const [open, setOpen] = useState(false);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [croppedImage, setCroppedImage] = useState(null);
    const [objectUrl, setObjectUrl] = useState(null);
    const [inputDisabled, setInputDisabled] = useState(false);

    // Extract individual state values from formState
    const { heading, description, link, callToAction, mediaType, videoLink } = formState;

    // Other state
    const [loading, setLoading] = useState(false);
    const [cardDetails, setCardDetails] = useState(null);
    const { cardId } = useParams();
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    const { cardState, updateCardData } = useCardContext();

    // Combined function to update formState
    const updateFormState = useCallback((updates) => {
        setFormState(prev => ({ ...prev, ...updates }));
    }, []);

    // Image cropping functions
    const calculateInitialZoom = useCallback((mediaWidth, mediaHeight) => {
        const containerWidth = window.innerWidth * 0.9;
        const containerHeight = window.innerHeight * 0.9;
        const widthRatio = containerWidth / mediaWidth;
        const heightRatio = containerHeight / mediaHeight;
        return Math.min(widthRatio, heightRatio);
    }, []);

    const onMediaLoaded = useCallback((mediaSize) => {
        const initialZoom = calculateInitialZoom(mediaSize.width, mediaSize.height);
        setZoom(initialZoom);
        setCrop({ x: 0, y: 0 });
    }, [calculateInitialZoom]);

    const handleImageChange = (event) => {
        if (event.target.files && event.target.files[0]) {
            const file = URL.createObjectURL(event.target.files[0]);
            setObjectUrl(file);
            setImage(file);
            setOpen(true);
            event.target.value = '';
        }
    };

    const handleRemoveImage = () => {
        setCroppedImage(null);
        setImage(null);

        if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
            setObjectUrl(null);
        }

        setInputDisabled(true);
        setTimeout(() => setInputDisabled(false), 0);
    };

    const handleClose = () => {
        setOpen(false);
        setImage(null);
        setObjectUrl(null);
        setZoom(1);
    };

    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const handleSave = async () => {
        try {
            const croppedImageBlob = await getCroppedImg(
                image,
                croppedAreaPixels,
                'rectangle',
                'product-image'
            );

            const croppedImageUrl = URL.createObjectURL(croppedImageBlob);
            setCroppedImage(croppedImageUrl);
            setOpen(false);
        } catch (e) {
            console.error(e);
        }
    };

    // Fetch card details only once
    const fetchCardDetails = useCallback(async () => {
        if (fetchedCardDetailsRef.current || !cardId) return;

        try {
            setLoading(true);
            console.log("Fetching card details for ID:", cardId);
            const data = await getCardById(cardId);
            setCardDetails(data);
            setLoading(false);
            fetchedCardDetailsRef.current = true;
        } catch (error) {
            console.error('Error fetching card details:', error);
            setLoading(false);
        }
    }, [cardId]);

    // Fetch existing card data for editing (only once)
    useEffect(() => {
        fetchCardDetails();
    }, [fetchCardDetails]);

    // Populate fields from card details (only once)
    useEffect(() => {
        if (cardDetails?.card && !initializedRef.current) {
            console.log("Initializing form with card details:", cardDetails.card._id);

            // Update all form state at once to prevent multiple re-renders
            updateFormState({
                heading: cardDetails.card.products_and_services?.heading || '',
                description: cardDetails.card.products_and_services?.description || '',
                link: cardDetails.card.products_and_services?.link || '',
                callToAction: cardDetails.card.products_and_services?.callToAction || '',
                mediaType: cardDetails.card.products_and_services?.mediaType || 'image',
                videoLink: cardDetails.card.products_and_services?.videoLink || ''
            });

            // Mark as initialized
            initializedRef.current = true;
        }
    }, [cardDetails, updateFormState]);

    // Load data from context if available (only once)
    useEffect(() => {
        if (cardState[cardId] && !initializedRef.current) {
            updateFormState({
                heading: cardState[cardId].productsAndServices?.heading || '',
                description: cardState[cardId].productsAndServices?.description || '',
                link: cardState[cardId].productsAndServices?.link || '',
                callToAction: cardState[cardId].productsAndServices?.callToAction || '',
                mediaType: cardState[cardId].productsAndServices?.mediaType || 'image',
                videoLink: cardState[cardId].productsAndServices?.videoLink || ''
            });

            // Mark as initialized
            initializedRef.current = true;
        }
    }, [cardState, cardId, updateFormState]);

    // Handle input changes
    const handleInputChange = useCallback((event) => {
        const { name, value } = event.target;
        updateFormState({ [name]: value });
    }, [updateFormState]);

    // Form submission handler
    const handleSubmit = useCallback(async (event) => {
        event.preventDefault();

        // Save current form data to context
        const formData = {
            productsAndServices: {
                heading,
                description,
                link,
                callToAction,
                mediaType,
                videoLink
            }
        };

        updateCardData(cardId, formData);

        // Update the card in the database
        setLoading(true);

        try {
            if (cardDetails?.card?._id) {
                const cardUpdateDetails = {
                    products_and_services: {
                        heading,
                        description,
                        link,
                        callToAction,
                        mediaType,
                        videoLink
                    }
                };

                await updateCard(cardDetails.card._id, cardUpdateDetails);

                setSnackbarMessage('Products and Services updated successfully');
                setSnackbarSeverity('success');
                setSnackbarOpen(true);
            }
        } catch (error) {
            console.error('Error updating products and services:', error);
            setSnackbarMessage('Failed to update products and services. Please try again.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setLoading(false);
        }
    }, [cardId, heading, description, link, callToAction, mediaType, videoLink, cardDetails?.card?._id, updateCardData]);

    // Handle snackbar close
    const handleSnackbarClose = useCallback(() => {
        setSnackbarOpen(false);
    }, []);

    return (
        <>
            <NotificationSnackbar
                open={snackbarOpen}
                message={snackbarMessage}
                handleClose={handleSnackbarClose}
                severity={snackbarSeverity}
            />

            <form onSubmit={handleSubmit}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        padding: 2,
                        backgroundColor: '#ffffff'
                    }}
                >
                    {/* Header Section */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            alignItems: 'center',
                            marginBottom: 2,
                        }}
                    >
                        <Typography variant="body2" sx={{ fontSize: '1.1rem', fontWeight: 600, color: '#4f4f4f', marginBottom: '10px' }} gutterBottom align="left">
                            Products & Services
                        </Typography>
                    </Box>

                    {/* Content Section */}
                    <Box
                        sx={{
                            flex: 1,
                            overflowY: 'auto',
                            padding: 2,
                            borderRadius: 1,
                        }}
                    >
                        <Grid container spacing={2}>
                            {/* Heading Field */}
                            <Grid item xs={12}>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginBottom: "10px" }}>
                                    Heading
                                </Typography>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    placeholder="Enter heading"
                                    name="heading"
                                    value={heading}
                                    onChange={handleInputChange}
                                    sx={{
                                        backgroundColor: '#f7f7f7',
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                border: 'none',
                                            },
                                            '&:hover fieldset': {
                                                border: 'none',
                                            },
                                            '&.Mui-focused fieldset': {
                                                border: 'none',
                                            },
                                            '& input::placeholder': {
                                                fontSize: '0.8rem',
                                                fontWeight: '300',
                                            },
                                        },
                                    }}
                                />
                            </Grid>

                            {/* Description Field */}
                            <Grid item xs={12}>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginBottom: "10px" }}>
                                    Description
                                </Typography>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    variant="outlined"
                                    placeholder="Enter description"
                                    name="description"
                                    value={description}
                                    onChange={handleInputChange}
                                    sx={{
                                        backgroundColor: '#f7f7f7',
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                border: 'none',
                                            },
                                            '&:hover fieldset': {
                                                border: 'none',
                                            },
                                            '&.Mui-focused fieldset': {
                                                border: 'none',
                                            },
                                            '& input::placeholder': {
                                                fontSize: '0.8rem',
                                                fontWeight: '300',
                                            },
                                        },
                                    }}
                                />
                            </Grid>

                            {/* Link Field */}
                            <Grid item xs={12}>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginBottom: "10px" }}>
                                    Link
                                </Typography>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    placeholder="Enter link URL"
                                    name="link"
                                    value={link}
                                    onChange={handleInputChange}
                                    sx={{
                                        backgroundColor: '#f7f7f7',
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                border: 'none',
                                            },
                                            '&:hover fieldset': {
                                                border: 'none',
                                            },
                                            '&.Mui-focused fieldset': {
                                                border: 'none',
                                            },
                                            '& input::placeholder': {
                                                fontSize: '0.8rem',
                                                fontWeight: '300',
                                            },
                                        },
                                    }}
                                />
                            </Grid>

                            {/* Call To Action Field */}
                            <Grid item xs={12}>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginBottom: "10px" }}>
                                    Call To Action
                                </Typography>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    placeholder="Enter call to action text"
                                    name="callToAction"
                                    value={callToAction}
                                    onChange={handleInputChange}
                                    sx={{
                                        backgroundColor: '#f7f7f7',
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                                border: 'none',
                                            },
                                            '&:hover fieldset': {
                                                border: 'none',
                                            },
                                            '&.Mui-focused fieldset': {
                                                border: 'none',
                                            },
                                            '& input::placeholder': {
                                                fontSize: '0.8rem',
                                                fontWeight: '300',
                                            },
                                        },
                                    }}
                                />
                            </Grid>

                            {/* Media Type Section */}
                            <Grid item xs={12}>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginBottom: "10px" }}>
                                    Media Type
                                </Typography>
                                <FormControl>
                                    <RadioGroup
                                        row
                                        name="mediaType"
                                        value={mediaType}
                                        onChange={handleInputChange}
                                        sx={{
                                            '& .MuiFormControlLabel-root': {
                                                marginRight: 4
                                            }
                                        }}
                                    >
                                        <FormControlLabel
                                            value="image"
                                            control={<Radio size="small" />}
                                            label={
                                                <Typography sx={{ fontSize: '0.8rem', color: "#4f4f4f" }}>
                                                    Image
                                                </Typography>
                                            }
                                        />
                                        <FormControlLabel
                                            value="video"
                                            control={<Radio size="small" />}
                                            label={
                                                <Typography sx={{ fontSize: '0.8rem', color: "#4f4f4f" }}>
                                                    Video
                                                </Typography>
                                            }
                                        />
                                    </RadioGroup>
                                </FormControl>
                            </Grid>

                            {/* Image Upload Section - Only show when image is selected */}
                            {mediaType === 'image' && (
                                <Grid item xs={12}>
                                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginBottom: "10px", display: 'flex', alignItems: 'center' }}>
                                        Upload Image
                                        <Tooltip title="Ideal Dimensions: 780 px by 300 px (2.1:1)" arrow>
                                            <InfoOutlinedIcon sx={{ fontSize: '1rem', marginLeft: 0.5 }} />
                                        </Tooltip>
                                    </Typography>
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            width: '100%',
                                            height: 200,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: '100%',
                                                height: '100%',
                                                borderRadius: '8px',
                                                backgroundColor: '#f2f2f2',
                                                border: '1px solid #dbdbdb',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                position: 'relative',
                                                textAlign: 'center',
                                                padding: 1,
                                                backgroundImage: croppedImage ? `url(${croppedImage})` : 'none',
                                                backgroundSize: 'cover',
                                                backgroundPosition: 'center',
                                            }}
                                            component="label"
                                        >
                                            {!croppedImage && (
                                                <>
                                                    <InsertPhotoOutlinedIcon sx={{ fontSize: '40px', color: '#828282', marginBottom: '10px' }} />
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ fontSize: '0.8rem', fontWeight: 400, color: '#212121' }}
                                                    >
                                                        <span style={{ color: 'blue' }}>Select</span> image file or drag and drop one here
                                                    </Typography>
                                                </>
                                            )}

                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleImageChange}
                                                disabled={inputDisabled}
                                                style={{
                                                    position: 'absolute',
                                                    width: '100%',
                                                    height: '100%',
                                                    top: 0,
                                                    left: 0,
                                                    opacity: 0,
                                                    cursor: 'pointer',
                                                    pointerEvents: croppedImage ? 'none' : 'auto',
                                                }}
                                            />
                                        </Box>

                                        {/* Close Button */}
                                        {croppedImage && (
                                            <IconButton
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveImage();
                                                }}
                                                sx={{
                                                    position: 'absolute',
                                                    top: 8,
                                                    right: 8,
                                                    color: '#828282',
                                                    backgroundColor: 'rgb(255, 255, 255)',
                                                    '&:hover': { backgroundColor: 'rgb(255, 255, 255)' },
                                                    width: 26,
                                                    height: 26,
                                                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                                                    zIndex: 2,
                                                }}
                                            >
                                                <CloseIcon sx={{ fontSize: 12 }} />
                                            </IconButton>
                                        )}
                                    </Box>
                                </Grid>
                            )}

                            {/* Video Link Field - Only show when video is selected */}
                            {mediaType === 'video' && (
                                <Grid item xs={12}>
                                    <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 600, color: "#4f4f4f", marginBottom: "10px" }}>
                                        Enter Video Link
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        variant="outlined"
                                        placeholder="Enter video link"
                                        name="videoLink"
                                        value={videoLink}
                                        onChange={handleInputChange}
                                        sx={{
                                            backgroundColor: '#f7f7f7',
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    border: 'none',
                                                },
                                                '&:hover fieldset': {
                                                    border: 'none',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    border: 'none',
                                                },
                                                '& input::placeholder': {
                                                    fontSize: '0.8rem',
                                                    fontWeight: '300',
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Box>

                    {/* Footer Section */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 1,
                            padding: 2,
                            borderTop: '1px solid #e0e0e0',
                        }}
                    >
                        <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            sx={{
                                backgroundColor: '#ffffff',
                                borderColor: '#dedede',
                                border: "0.75px solid",
                                padding: '5px 16px',
                                color: '#212121',
                                textTransform: 'none',
                                borderRadius: '10px',
                                marginRight: "4px",
                                '&:hover': {
                                    backgroundColor: '#f7f7f7',
                                    borderColor: '#dedede',
                                },
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            type="submit"
                            disabled={loading}
                            sx={{
                                backgroundColor: '#000000',
                                padding: '5px 32px',
                                color: '#ffffff',
                                fontWeight: "bold",
                                textTransform: 'none',
                                borderRadius: '10px',
                                '&:hover': {
                                    backgroundColor: '#333333',
                                },
                            }}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : "Save"}
                        </Button>
                    </Box>
                </Box>
            </form>

            {/* Image Cropping Modal */}
            <Modal
                open={open}
                onClose={handleClose}
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backdropFilter: 'blur(7px)',
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                }}
            >
                <Box
                    sx={{
                        outline: 'none',
                        width: '90vw',
                        maxWidth: '900px',
                        height: '90vh',
                        maxHeight: '700px',
                    }}
                >
                    {image && (
                        <>
                            <Box
                                sx={{
                                    position: 'relative',
                                    width: '100%',
                                    height: 'calc(100% - 100px)',
                                    marginBottom: 2,
                                }}
                            >
                                <Cropper
                                    image={image}
                                    crop={crop}
                                    zoom={zoom}
                                    aspect={2.6}
                                    cropShape="rect"
                                    onCropChange={setCrop}
                                    onZoomChange={setZoom}
                                    onCropComplete={onCropComplete}
                                    onMediaLoaded={onMediaLoaded}
                                    objectFit="contain"
                                    centerImage={true}
                                    showGrid={false}
                                    minZoom={0.1}
                                    maxZoom={3}
                                    restrictPosition={false}
                                />
                            </Box>

                            <Box sx={{ textAlign: 'center' }}>
                                <Slider
                                    value={zoom}
                                    onChange={(e, newValue) => setZoom(newValue)}
                                    min={0.1}
                                    max={3}
                                    step={0.01}
                                    valueLabelDisplay="auto"
                                    defaultValue={1}
                                    sx={{
                                        marginBottom: 2,
                                        width: '80%',
                                        margin: '0 auto 20px auto'
                                    }}
                                />
                                <div>
                                    <Button
                                        onClick={handleClose}
                                        variant="outlined"
                                        sx={{
                                            marginRight: 2,
                                            backgroundColor: '#ffffff',
                                            borderColor: '#dedede',
                                            border: "0.75px solid",
                                            padding: '5px 16px',
                                            color: '#212121',
                                            textTransform: 'none',
                                            borderRadius: '10px',
                                            '&:hover': {
                                                backgroundColor: '#f7f7f7',
                                                borderColor: '#dedede',
                                            },
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={handleSave}
                                        variant="contained"
                                        sx={{
                                            backgroundColor: '#000000',
                                            padding: '5px 32px',
                                            color: '#ffffff',
                                            fontWeight: "bold",
                                            textTransform: 'none',
                                            borderRadius: '10px',
                                            '&:hover': {
                                                backgroundColor: '#333333',
                                            },
                                        }}
                                    >
                                        Crop
                                    </Button>
                                </div>
                            </Box>
                        </>
                    )}
                </Box>
            </Modal>
        </>
    );
};

export default ProductsAndServicesContainer; 